import { useRecoilState, useSetRecoilState } from "recoil";
import { apiCallErrorAtom, authAtom } from "../state";
import { API_URL, GLOBAL_HTTP_ERROR_CODES } from "../constants";

const request = (method, { auth, setAuth, onApiCallError }) => {
  return async (url, body) => {
    const requestOptions = {
      method,
      headers: { Authorization: `Bearer ${auth.idToken}` },
    };
    if (body) {
      requestOptions.headers["Content-Type"] = "application/json";
      requestOptions.body = JSON.stringify(body);
    }

    try {
      let response = await fetch(url, requestOptions);

      // If we get an authentication error then attempt to renew the token
      if (response.status === 401) {
        const renewTokenOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            accessToken: auth.accessToken,
            refreshToken: auth.refreshToken,
          }),
        };

        response = await fetch(
          `${API_URL}/auth/refresh-token`,
          renewTokenOptions
        );

        // If the token was successfully renewed then update auth state and try again
        if (response.ok) {
          const jsonResponse = await response.json();
          setAuth({ ...auth, ...jsonResponse });
          requestOptions.headers[
            "Authorization"
          ] = `Bearer ${jsonResponse.idToken}`;
          return handleResponse(
            await fetch(url, requestOptions),
            onApiCallError
          );
        }
      }

      return handleResponse(response, onApiCallError);
    } catch (error) {
      onApiCallError(500, error.toString());
      return Promise.reject(null);
    }
  };
};

const handleResponse = async (response, onApiCallError) => {
  let data = await response.text();

  if (data) {
    data = JSON.parse(data);
  }

  if (!response.ok) {
    const error = (data && data.error) || response.statusText;
    onApiCallError(response.status, error);
    return Promise.reject(error);
  }

  return data;
};

const isLoggedIn = (auth) => {
  return auth && auth.idToken;
};

const useApiClient = () => {
  const [auth, setAuth] = useRecoilState(authAtom);
  const setApiCallError = useSetRecoilState(apiCallErrorAtom);

  const onApiCallError = (httpCode, message) => {
    // We're only interested in handling those errors that have an impact application wide.
    // TODO: This is not the ideal solution as each user of the API client should have a way to specify
    //       what error codes wants to handle, but this is not possible at the moment as the session API client is
    //       global and part of the Recoil state
    if (GLOBAL_HTTP_ERROR_CODES.indexOf(httpCode) !== -1) {
      setApiCallError({ message, httpCode });
    }
  };

  return {
    get: request("GET", { auth, setAuth, onApiCallError }),
    post: request("POST", { auth, setAuth, onApiCallError }),
    patch: request("PATCH", { auth, setAuth, onApiCallError }),
  };
};

export { isLoggedIn, useApiClient };
export default useApiClient;
